import { useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Paperclip, X, DownloadSimple, FileImage, FilePdf, File as FileIcon, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILE_ICON = (ct) => {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct === "application/pdf") return FilePdf;
  return FileIcon;
};

const humanSize = (n) => {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
};

export default function AttachmentPanel({ ticketId, attachments = [], onChange, currentUserId, currentUserRole }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const canDelete = (att) => {
    if (!ticketId) return false;
    if (currentUserRole === "admin" || currentUserRole === "technicien") return true;
    return att.uploader_id === currentUserId;
  };

  const remove = async (att) => {
    if (!window.confirm(`Supprimer la pièce jointe « ${att.original_filename} » ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/tickets/${ticketId}/attachments/${att.id}`);
      toast.success("Pièce jointe supprimée");
      onChange?.({ removed: att.id });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    } finally { setBusy(false); }
  };

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/tickets/${ticketId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange?.(r.data);
      }
      toast.success(`${fileList.length} pièce${fileList.length > 1 ? "s" : ""} jointe${fileList.length > 1 ? "s" : ""} ajoutée${fileList.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="space-y-3" data-testid="attachments-panel">
      <div className="flex items-center justify-between">
        <div className="label-caps flex items-center gap-2"><Paperclip size={12}/> Pièces jointes ({attachments.filter((a) => a.content_type !== "message/rfc822").length})</div>
        <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="attach-upload-btn">
          <Paperclip size={14} className="mr-1.5"/>{busy ? "Envoi…" : "Ajouter"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          data-testid="attach-file-input"
          onChange={(e) => upload(e.target.files)}
        />
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune pièce jointe. Vous pouvez déposer captures d&apos;écran, PDF ou logs (max 15 Mo).</p>
      ) : (
        <>
          {attachments.filter((a) => a.content_type === "message/rfc822").length > 0 && (
            <div
              data-testid="original-eml-banner"
              className="mb-2 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/60 dark:bg-blue-950/30"
              title="Le mail original importé depuis la boîte mail — ouvrez-le dans Outlook si les images ne s'affichent pas dans le ticket"
            >
              <span className="text-xl leading-none">📧</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Mail original disponible
                </div>
                <div className="text-[0.7rem] text-blue-800/80 dark:text-blue-200/80">
                  Si les images ne s&apos;affichent pas dans le corps du ticket, téléchargez le fichier <span className="mono">.eml</span> ci-dessous et double-cliquez : il s&apos;ouvre dans Outlook avec le rendu d&apos;origine.
                </div>
              </div>
              <button
                onClick={() => download(attachments.find((a) => a.content_type === "message/rfc822"))}
                data-testid="download-original-eml"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 whitespace-nowrap"
              >
                Ouvrir dans Outlook
              </button>
            </div>
          )}
          <ul className="space-y-2">
            {attachments.filter((a) => a.content_type !== "message/rfc822").map((a) => {
              const Icon = FILE_ICON(a.content_type);
              return (
                <li key={a.id}
                  data-testid={`attach-${a.id}`}
                  className="flex items-center gap-3 p-2 rounded-md border bg-muted/30 hover:bg-muted/60 transition-colors">
                  <Icon size={18} className="text-muted-foreground"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {a.original_filename}
                    </div>
                    <div className="text-[0.68rem] text-muted-foreground mono">{humanSize(a.size)} · {a.uploader_name}</div>
                  </div>
                  <button
                    onClick={() => download(a)}
                    data-testid={`attach-dl-${a.id}`}
                    className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground">
                    <DownloadSimple size={16}/>
                  </button>
                  {canDelete(a) && (
                    <button
                      onClick={() => remove(a)}
                      disabled={busy}
                      data-testid={`attach-del-${a.id}`}
                      title="Supprimer"
                      className="p-1.5 rounded hover:bg-red-100 text-muted-foreground hover:text-red-700 disabled:opacity-40 dark:hover:bg-red-950/30">
                      <Trash size={16}/>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
